import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonRequests = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Consuming APIs with Requests</h1>
                <p className="subtitle">HTTP for Humans. How to make your Python scripts talk to the rest of the internet.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Your Python script is stuck inside your computer. To get data from Instagram, Google, or ChatGPT, it needs to go online.
                    </p>
                    <ul>
                        <li><strong>The Browser:</strong> When you open Chrome and type <code>google.com</code>, you are sending a <strong>GET Request</strong>. The server sends back HTML.</li>
                        <li><strong>Requests Library:</strong> This library lets your Python script act like a Browser. It can "visit" web pages, "fill out" forms (POST), and download files, all without a screen.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Basics: GET and POST</h2>
                <p>
                    99% of the web is just these two verbs.
                </p>
                <CodeBlock language="python" code={`import requests

# 1. GET: "Give me data"
# We ask GitHub for info about a user
resp = requests.get("https://api.github.com/users/rohit")

if resp.status_code == 200:
    data = resp.json() # Parse JSON automatically
    print(f"User found: {data['name']}")
    
# 2. POST: "Here is new data"
# We send a dictionary (JSON) to a server
payload = {"username": "rohit", "message": "hello world"}
resp = requests.post("https://httpbin.org/post", json=payload)

print(resp.json()) # Server confirms it received our data`} />
            </div>

            <div className="content-section">
                <h2>3. Headers: The ID Card</h2>
                <p>
                    Some websites block scripts. To pretend to be a real browser, you need to send a <strong>User-Agent</strong> Header.
                </p>
                <CodeBlock language="python" code={`headers = {
    "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64)...",
    "Authorization": "Bearer my-secret-api-token"
}

requests.get("https://api.netflix.com/movies", headers=headers)`} />
            </div>

            <div className="content-section">
                <h2>4. Deep Dive: Performance (Session Objects)</h2>
                <p>
                    Beginners use <code>requests.get()</code> directly. Professionals use <strong>Sessions</strong>.
                    <br />
                    <strong>Why?</strong> Creating a connection (TCP Handshake + SSL) is slow. A Session <strong>keeps the connection open</strong> (Keep-Alive) for subsequent requests. It is 5x-10x faster for bulk operations.
                </p>
                <CodeBlock language="python" code={`#  SLOW: Opens and closes 10 separate connections
for i in range(10):
    requests.get("https://api.my-app.com/data")

# FAST: Reuses 1 connection
session = requests.Session()
for i in range(10):
    session.get("https://api.my-app.com/data")`} />
            </div>

            <div className="content-section">
                <h2>5. Production Safety: Timeouts & Retries</h2>
                <p>
                    The internet is unreliable. Without a <strong>Timeout</strong>, your script might hang <strong>FOREVER</strong> if a server stops responding.
                </p>
                <CodeBlock language="python" code={`from requests.adapters import HTTPAdapter
from urllib3.util.retry import Retry

# 1. Define Retry Logic
# "Try 3 times. Wait 1s, then 2s, then 4s (Backoff)"
retry_strategy = Retry(
    total=3,
    backoff_factor=1,
    status_forcelist=[500, 502, 503, 504]
)

# 2. Mount it to a Session
session = requests.Session()
adapter = HTTPAdapter(max_retries=retry_strategy)
session.mount("https://", adapter)

# 3. Use it with a Timeout!
try:
    # If server takes &gt; 5s, it raises a Timeout Error
    session.get("https://api.slow-site.com", timeout=5)
except requests.exceptions.Timeout:
    print("Server is too slow!")`} />
            </div>

        </div>
    );
};

export default PythonRequests;


